import * as THREE from 'three';

type SplashDrop = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
};

const LIFE = 0.52;

/** Expanding ring of water droplets when entering/exiting water or swimming fast. */
export class FpsWaterSplash {
  private readonly drops: SplashDrop[] = [];
  private readonly geo = new THREE.SphereGeometry(0.032, 4, 4);
  private readonly colors = [0xa8e8ff, 0xd4f4ff, 0x7ec8e8, 0xe8fcff];

  constructor(private readonly scene: THREE.Scene) {}

  spawn(x: number, y: number, z: number): void {
    const ringCount = 14 + Math.floor(Math.random() * 5);
    for (let i = 0; i < ringCount; i++) {
      const angle = (i / ringCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.35;
      const mat = new THREE.MeshBasicMaterial({
        color: this.colors[i % this.colors.length]!,
        transparent: true,
        opacity: 0.72,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(this.geo, mat);
      const ringR = 0.08 + Math.random() * 0.12;
      mesh.position.set(
        x + Math.cos(angle) * ringR,
        y + 0.02 + Math.random() * 0.04,
        z + Math.sin(angle) * ringR,
      );
      mesh.scale.setScalar(0.55 + Math.random() * 0.35);
      this.scene.add(mesh);

      const spread = 1.1 + Math.random() * 1.4;
      const vel = new THREE.Vector3(
        Math.cos(angle) * spread,
        0.55 + Math.random() * 0.75,
        Math.sin(angle) * spread,
      );

      this.drops.push({
        mesh,
        vel,
        life: 0,
        maxLife: LIFE * (0.82 + Math.random() * 0.36),
      });
    }

    const centerCount = 2 + Math.floor(Math.random() * 3);
    for (let i = 0; i < centerCount; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xe8fcff,
        transparent: true,
        opacity: 0.65,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(this.geo, mat);
      mesh.position.set(
        x + (Math.random() - 0.5) * 0.18,
        y + 0.04,
        z + (Math.random() - 0.5) * 0.18,
      );
      mesh.scale.setScalar(0.7 + Math.random() * 0.4);
      this.scene.add(mesh);
      this.drops.push({
        mesh,
        vel: new THREE.Vector3(
          (Math.random() - 0.5) * 0.6,
          1.0 + Math.random() * 0.9,
          (Math.random() - 0.5) * 0.6,
        ),
        life: 0,
        maxLife: LIFE * 0.75,
      });
    }
  }

  update(dt: number): void {
    for (let i = this.drops.length - 1; i >= 0; i--) {
      const d = this.drops[i]!;
      d.life += dt;
      d.mesh.position.addScaledVector(d.vel, dt);
      d.vel.y -= dt * 4.8;
      d.vel.multiplyScalar(1 - dt * 1.6);
      const t = d.life / d.maxLife;
      const mat = d.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.max(0, 0.72 * (1 - t * t));
      d.mesh.scale.setScalar((0.55 + t * 0.85) * (1 - t * 0.2));
      if (d.life >= d.maxLife) {
        this.scene.remove(d.mesh);
        mat.dispose();
        this.drops.splice(i, 1);
      }
    }
  }

  dispose(): void {
    for (const d of this.drops) {
      this.scene.remove(d.mesh);
      (d.mesh.material as THREE.Material).dispose();
    }
    this.drops.length = 0;
    this.geo.dispose();
  }
}
